/**
 * Native (Capacitor) app bootstrap.
 *
 * `initNativeApp` is called once from main.tsx. In the browser it is a
 * no-op; inside the native iOS / Android shell it wires the pieces that
 * make the wrapped PWA behave like a real app: status-bar styling, the
 * Android hardware back button, OS deep links, and hiding the launch
 * splash once the first frame has painted.
 *
 * Native push and native camera capture are deliberately NOT wired here —
 * they are tracked follow-ups (see docs/capacitor-mobile.md).
 */
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

import { isAndroid, isNativePlatform } from './platform';

interface NativeBridgeOptions {
  /** Navigate the SPA to an in-app path, e.g. "/parent/dancers". */
  navigate: (path: string) => void;
}

/**
 * Exact hosts a Pirouette deep link is allowed to target. A deep link is
 * untrusted input handed over by the OS, so `extractInAppPath` follows only
 * https links whose host is on this explicit allowlist — never an arbitrary
 * subdomain, which could redirect the app somewhere unexpected.
 *
 * This set MUST stay in sync with the hosts declared in the App Links
 * (`assetlinks.json`) and Universal Links (`apple-app-site-association`)
 * association files — see docs/capacitor-mobile.md follow-up #4. Adjust it
 * to the real app host before deep links are enabled.
 */
const ALLOWED_DEEP_LINK_HOSTS: ReadonlySet<string> = new Set([
  'pirouettehq.com',
  'app.pirouettehq.com',
]);

/**
 * Validate an OS deep-link URL and return the in-app path to navigate to,
 * or `null` if the link is not one we should follow.
 *
 * Exported for unit testing — this is the security boundary for deep links.
 */
export function extractInAppPath(rawUrl: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  // Only follow https links to a known host. App Links (Android) and
  // Universal Links (iOS) are always https, so this covers every real deep
  // link while rejecting foreign hosts and non-https schemes outright.
  if (parsed.protocol !== 'https:') return null;
  if (!ALLOWED_DEEP_LINK_HOSTS.has(parsed.hostname.toLowerCase())) return null;

  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  // Require a single-slash absolute path: reject protocol-relative ("//host")
  // and other malformed values before handing them to the router.
  return path.startsWith('/') && !path.startsWith('//') ? path : null;
}

let bridgeInitialised = false;

/**
 * Wire native-shell behaviour. Safe to call unconditionally — it returns
 * immediately in the browser and is idempotent on native.
 */
export async function initNativeApp(options: NativeBridgeOptions): Promise<void> {
  if (!isNativePlatform() || bridgeInitialised) return;
  bridgeInitialised = true;

  // Status bar — the app is always light-themed (#ffffff), so force dark
  // status-bar content regardless of the OS light/dark setting.
  try {
    await StatusBar.setStyle({ style: Style.Light });
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }
  } catch {
    // Status-bar styling is best-effort; never block app start on it.
  }

  // The listeners below live for the lifetime of the app process — there is
  // no teardown path, and the `bridgeInitialised` guard above prevents any
  // duplicate registration.

  // Android hardware back button — step back through history, or send the
  // app to the background at the first screen (never hard-kill it).
  void CapacitorApp.addListener('backButton', () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      void CapacitorApp.minimizeApp();
    }
  });

  // OS deep links — follow validated links to the matching in-app route.
  // This handles WARM deep links (app already running, router mounted).
  // Cold-start launch URLs need App.getLaunchUrl() read after the router is
  // mounted — deferred to the App Links work (docs/capacitor-mobile.md #4),
  // since no https deep link reaches the app until domain association exists.
  void CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    const path = extractInAppPath(url);
    if (path) options.navigate(path);
  });

  // Hide the launch splash once the first frame has painted, so there is no
  // white flash between the splash and the rendered app.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void SplashScreen.hide();
    });
  });
}
